import type { DossierResponse } from '@/features/dossier/api';
import { DossierProvenanceHeader } from '@/features/dossier/components/DossierResultRenderer';

// ── AU TGA class label ────────────────────────────────────────────────────────

/**
 * AU TGA device class label. Medical-device ARTG classes are Class I/IIa/IIb/III;
 * IVD classes are the numerals Class 2/3/4 (AU IVDs are a subcategory under the
 * same TG(MD)Regs 2002, classed 2/3/4). Accepts the raw class token (e.g. "IIa",
 * "2") and the prefixed submission_type form (e.g. "au-class-iia",
 * "au-ivd-class-2"). Never carries "MHRA" / "Health Canada" framing — AU/TGA-only.
 */
export function auClassLabel(t: string | null | undefined): string {
  if (!t) return '';
  // Strip optional "au-class-" / "au-ivd-class-" prefix, then normalise case.
  const raw = t.replace(/^au-(?:ivd-)?class-/i, '');
  const norm = raw.toUpperCase();
  if (norm === 'I') return 'Class I';
  if (norm === 'IIA') return 'Class IIa';
  if (norm === 'IIB') return 'Class IIb';
  if (norm === 'III') return 'Class III';
  // AU IVD numeral classes.
  if (norm === '2') return 'Class 2';
  if (norm === '3') return 'Class 3';
  if (norm === '4') return 'Class 4';
  return t; // Passthrough for unrecognised values.
}

// ── AU TGA submission provenance header ───────────────────────────────────────

/** The TGA ARTG submission header — now the shared {@link DossierProvenanceHeader}
 *  (device · type · pathway · generated · by). The AU applicant identity
 *  (Sponsor · ARTG number · signatory · contact) is account-sourced and lives in
 *  the generated PDF's submission body, not this in-app review card. */
export function AuDossierHeader({ response }: { response: DossierResponse }) {
  return (
    <DossierProvenanceHeader
      response={response}
      eyebrow="TGA ARTG Submission Dossier"
      pathwayLabel={auClassLabel(response.submission_type)}
    />
  );
}
